import {
	Box,
	Button,
	Grid,
	Skeleton,
	Theme,
	Typography
} from "@mui/material";
import BreadCrum from "../../../lib/BreadCrum";
import { useHistory } from "react-router";
import useQuery from "../../../utils/useQuery";
import {
	useEffect,
	useState
} from "react";
import { RootStateType } from "../../../redux-store/reducers";
import {
	useDispatch,
	useSelector
} from "react-redux";
import { navigationData as NavigationData, PaginationObj, SortingObj, TableHeaderData } from "../types";
import {
	ChartIcon,
	CurrencyIcon,
	MoneyGrow,
	PercentageIcons,
	TotalInvestors
} from "../../../assets/SvgIcons";
import {
	DirectEquityData,
	FetchHoldingDetailsDataArgs,
	HoldingDetailsTableObj,
	InfoCardProps,
	PerformanceChartData,
	PerformersObj,
	PieChartData
} from "./types";
import DirectEquityOverview from "./directEquityOverview";
import { USER_ROLES } from "../../../utils/constant";
import { getBenchmarkPerformance, getHoldingDetailsFilter, getMarketCapAllocation, getSchemeDetails, getSectorAllocation, top10Schemes, totalCardValuesApi, getNonMfTransactionDetails } from "../../../redux-store/actions/product";
import { BenchmarkPerformanceRes, FetchDataArgs, GetHoldingDetailsFilterResponseBody, GetSchemeDetailsResponseBody, GetTop10SchemesRes, MarketCapAllocationRes, SectorAllocationResponseData, GetTransactionDetailsResponseBody, TotalCardValuesRes, TransactionDetailsData, getNonMfTranDetailsResBody, NonMfTransactionDetailsData } from "../../../redux-store/types/products";
import { formatCurrency } from "../../../utils/utilityFunctions";
import DirectEquityHoldingDetails from "./directEquityHoldingDetails";
import { ZeroStateInvestment } from "../common/ZeroStateInvestment";
import { cloneDeep } from "lodash";
import { InfoCardLoading } from "./LoadingComponents";
import NonMFTransactionDetails from "../common/NonMFTransactionDetails";

const initialSelectedHoldingTableHeadings: string[] = [
	"units",
	"investedValue",
	"exposure",
	"marketValue",
	"week52High",
	"marketCapitalisation",
	"dividend"
]

function InfoCard({
	Icon,
	title,
	value = 0,
	percentage = 0,
	valueTextColor = "text.primary",
	isLoading
}: InfoCardProps) {
	const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

	const formatedPercentage = !Number.isNaN(Number(percentage ?? 0)) ?
		Math.round((percentage ?? 0) * 100) / 100 : '0'

	return (
		<Box
			sx={(theme: Theme) => ({
				backgroundColor: theme.palette.common.white,
				borderRadius: "15px",
				boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
				padding: "25px 18px",
				display: "flex",
				height: "100%",
				alignItems: "center",
			})}
		>
			<Box
				sx={{
					flex: 1,
					display: "flex",
					justifyContent: "center",
				}}
			>
				<Icon />
			</Box>
			<Box
				sx={{
					flex: 3,
					display: "flex",
					flexDirection: "column",
					justifyContent: "flex-end",
					rowGap: "8px",
					width: '80%',
				}}
			>
				{isLoading && <InfoCardLoading />}
				{!isLoading && (
					<>
						<Typography
							variant="subtitle_500"
							sx={(theme: Theme) => ({
								lineHeight: "14px",
								color: theme.palette.text.primary,
							})}
						>
							{title}
						</Typography>
						<Typography
							variant="h3"
							sx={{
								lineHeight: "27.5px",
								color: valueTextColor
							}}
						>
							{title === "Market Value" && (
								(value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })}`
							)}
							{title === "Invested Value" && (
								(value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })}`
							)}
							{title === "Gain/Loss (%)" && (
								(value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })} (${formatedPercentage}%)` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })} (${formatedPercentage}%)`
							)}
							{title === "Absolute Return (%)" && (
								!Number.isNaN(Number(value ?? 0)) ?
									`${Math.round((value ?? 0) * 100) / 100} %` : '0 %'
							)}
							{title === "Total Investors" && (
								value ?? 0
							)}
						</Typography>
					</>
				)}
			</Box>

		</Box>
	)
}

const tabsList = ['Overview', 'Holding Details', 'Transaction Details']

export default function DirectEquity(): JSX.Element {
	const dispatch = useDispatch()

	const history = useHistory();

	const { pathname: pathName, state } = history.location
	const { iwappRmId = "", investorClientId = "", clientInterfaceCode = '' } = useSelector((store: RootStateType) => store.id)

	const navState = state as NavigationData
	const clientCode = investorClientId ?? navState?.clientCode ?? ""

	const query = useQuery()

	const initialRouter = pathName.split('/').filter((item: string) => item !== '')

	const { selectedPortfolio } = useSelector((store: RootStateType) => store.selectedPortfiolio);
	const { label: selectedPortfolioLabel = "", value: selectedPortfolioValue = "" } = selectedPortfolio

	const { role = '', permissions = [] } = useSelector((store: RootStateType) => store.auth);

	const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData)

	const rmRouterPath = [
		{
			name: 'Dashboard',
			path: '/dashboard',
		},
		...(!initialRouter.includes('product') ? [{
			name: `${initialRouter[1]} ${selectedPortfolioLabel ? `(${selectedPortfolioLabel})` : ''}`,
			path: `/${initialRouter[0]}/${initialRouter[1]}`,
			state: { clientCode }
		}] : []),
		{
			name: 'Direct Equity',
			path: !initialRouter.includes('product') ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}` : `/${initialRouter[0]}/${initialRouter[1]}}`,
			state: { clientCode }
		}
	]

	const investorRouterPath = [
		{
			name: `Dashboard ${selectedPortfolio?.label ? `(${selectedPortfolio.label})` : ''}`,
			path: '/investor-dashboard',
		},
		{
			name: 'Direct Equity',
			path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
			state: { clientCode }
		}
	]

	const initialDirectEquityDataArray = [
		{
			Icon: (props: React.SVGProps<SVGSVGElement>) => <MoneyGrow {...props} viewBox="0 0 57 50" />,
			title: "Market Value",
			value: 0
		},
		{
			Icon: (props: React.SVGProps<SVGSVGElement>) => <CurrencyIcon {...props} />,
			title: "Invested Value",
			value: 0
		},
		{
			Icon: (props: React.SVGProps<SVGSVGElement>) => <ChartIcon {...props} viewBox="0 0 57 50" />,
			title: "Gain/Loss (%)",
			value: 0,
			percentage: 0,
			valueTextColor: 'success.500'
		},
		{
			Icon: initialRouter.includes('product') ? (props: React.SVGProps<SVGSVGElement>) => <TotalInvestors {...props} viewBox="0 0 57 50" /> : (props: React.SVGProps<SVGSVGElement>) => <PercentageIcons {...props} viewBox="0 0 57 50" />,
			title: initialRouter.includes('product') ? "Total Investors" : "Absolute Return (%)",
			value: 0
		}
	]

	// Parent component
	const [isLoading, setLoading] = useState<boolean>(false)
	const [open, setOpen] = useState(false);
	const [selectedTab, setSelectedTab] = useState(query.get('tab') || 'Overview')
	const [directEquityDataObj, setDirectEquityDataObj] = useState<{ isSuccess: boolean; data: DirectEquityData[] }>({ isSuccess: true, data: initialDirectEquityDataArray })
	const [isDirectEquityDataArrayLoading, setDirectEquityDataArrayLoading] = useState<boolean>(false)

	// Overview component
	const [selectedDropdownOption, setSelectedDropdownOption] = useState<string>('1')
	const [isPieChartDataLoading, setPieChartDataLoading] = useState<boolean>(false)
	const [pieChartData, setPieChartData] = useState<PieChartData>({
		isSuccess: true,
		data: [],
		labelData: [],
		totalValue: { label: '', value: 0 }
	} as PieChartData)
	const [isTopFivePerformersDataLoading, setTopFivePerformersDataLoading] = useState<boolean>(false)
	const [topFivePerformersData, setTopFivePerformersData] = useState<PerformersObj>({ isSuccess: true, data: [] })
	const [isBottomFivePerformersDataLoading, setBottomFivePerformersDataLoading] = useState<boolean>(false)
	const [bottomFivePerformersData, setBottomFivePerformersData] = useState<PerformersObj>({ isSuccess: true, data: [] })
	const [activePerformanceChartButton, setActivePerformanceChartButton] = useState<string>('Inception');
	const [isPerformanceLoading, setPerformanceLoading] = useState<boolean>(false);
	const [performanceData, setPerformanceData] = useState<PerformanceChartData>({
		isSuccess: true,
		data1: [],
		data2: [],
		data3: [],
		data4: [],
		data5: [],
		label1: "",
		label2: "",
		label3: "",
		label4: "",
		label5: "",
	} as PerformanceChartData)
	const [filteredPerformanceData, setFilteredPerformanceData] = useState<PerformanceChartData>({
		isSuccess: true,
		data1: [],
		data2: [],
		data3: [],
		data4: [],
		data5: [],
		label1: "",
		label2: "",
		label3: "",
		label4: "",
		label5: "",
	} as PerformanceChartData)

	// Holding Details component
	const [isHoldingFilterDrawerOpen, setHoldingFilterDrawerOpen] = useState<boolean>(false);
	const [holdingTableHeaders, setHoldingTableHeaders] = useState<TableHeaderData[]>([])
	const [isHoldingTableHeadersLoading, setHoldingTableHeadersLoading] = useState<boolean>(false)
	const [appliedHoldingTableHeaders, setAppliedHoldingTableHeaders] = useState<TableHeaderData[]>([])
	const [holdingTableData, setHoldingTableData] = useState<HoldingDetailsTableObj>({
		isSuccess: true,
		data: []
	})
	const [isHoldingTableDataLoading, setHoldingTableDataLoading] = useState<boolean>(false)
	const [holdingSortingObj, setHoldingSortingObj] = useState<SortingObj>({
		order: "desc",
		orderBy: "marketValue"
	})
	const [holdingPaginationObj, setHoldingPaginationObj] = useState<PaginationObj>({
		currentPage: 0,
		rowsPerPage: 5,
		totalRows: 0
	})

	//transaction states and apis 
	const [transactionDetailsLoading, setTransactionDetailsLoading] = useState<boolean>(false);
	const [transactionTableData, setTransactionTableData] = useState<NonMfTransactionDetailsData[]>([]);
	const [allTransactionsData, setAllTransactionsData] = useState<NonMfTransactionDetailsData[]>([]);
	const [transactionDetailPaginationObj, setTransactionDetailPaginationObj] = useState<PaginationObj>({
		currentPage: 0,
		rowsPerPage: 5,
		totalRows: 0
	})
	const [transactionDetailsData, setTransactionDetailsData] = useState<getNonMfTranDetailsResBody>({} as getNonMfTranDetailsResBody)
	const investorProfile = useSelector((state: RootStateType) => state.investorProfile)
	const fetchTransactionDetailsData = async ({
		sortItem = "date",
		order = "desc",
		currentPage = 1,
		rowsPerPage = 5
	}: FetchDataArgs = {}) => {
		try {
			setTransactionDetailsLoading(true)

			const payload = {
				rmId: ![USER_ROLES.INVESTOR].includes(role) ? iwappRmId : '',
				clientCode: [USER_ROLES.INVESTOR].includes(role) ? investorClientId : pathName.includes('investor-profile') ? investorClientId ?? investorProfile?.clientCode ?? '' : ``,
				portfolio: [USER_ROLES.INVESTOR].includes(role) ? clientInterfaceCode ?? '' : pathName.includes('investor-profile') ? clientInterfaceCode ?? selectedPortfolio.value : ``,
				category: 'EQ',
				// sortItem,
				// order,
				// currentPage,
				// rowsPerPage,
			}

			const transactionDetailsRes = await (dispatch(getNonMfTransactionDetails(payload)) as unknown as Promise<getNonMfTranDetailsResBody>)
			setTransactionDetailsData(transactionDetailsRes)

			if (transactionDetailsRes?.isSuccess) {
				setTransactionTableData(transactionDetailsRes?.result);
				setAllTransactionsData(transactionDetailsRes?.result);
				setTransactionDetailPaginationObj(prevState => ({
					...prevState,
					totalRows: transactionDetailsRes?.totalLength
				}))
			} else {
				console.log(transactionDetailsRes?.message)
			}

		} catch (error) {
			console.log("Error: ", error)
		} finally {
			setTransactionDetailsLoading(false)
		}
	}

	const fetchDirectEquityData = async () => {
		try {
			setDirectEquityDataArrayLoading(true)

			const payload = {
				rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
				category: '',
				clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode : investorClientId,
				portfolioId: !initialRouter.includes('product') ? clientInterfaceCode ?? selectedPortfolio?.value : "",
				assetGroup: "EQ"
			}

			const { isSuccess, message, result } = await (dispatch(totalCardValuesApi(payload)) as unknown as Promise<TotalCardValuesRes>)

			if (isSuccess) {
				setDirectEquityDataObj(prevState => ({
					isSuccess: true,
					data: prevState.data.map((obj: DirectEquityData) => {
						if (obj.title === "Market Value") {
							return {
								...obj,
								value: result?.totalMarketValue ?? 0
							}
						}
						if (obj.title === "Invested Value") {
							return {
								...obj,
								value: result?.totalInvestedValue ?? 0
							}
						}
						if (obj.title === "Gain/Loss (%)") {
							return {
								...obj,
								value: result?.gainLoss ?? 0,
								percentage: result?.percentage ?? 0,
								valueTextColor: (result?.gainLoss ?? 0) >= 0 ? 'success.500' : 'error.500'
							}
						}
						if (obj.title === "Absolute Return (%)") {
							return {
								...obj,
								value: result?.irr ?? 0,
								valueTextColor: (result?.irr ?? 0) >= 0 ? 'success.500' : 'error.500'
							}
						}

						if (obj.title === "Total Investors") {
							return {
								...obj,
								value: result?.totalInvestors ?? 0
							}
						}
						return obj
					})
				}))
			} else {
				setDirectEquityDataObj({
					isSuccess: false,
					data: initialDirectEquityDataArray
				})
			}

			return isSuccess
		} catch (error) {
			console.log("Error: ", error)
		} finally {
			setDirectEquityDataArrayLoading(false)
		}
	}

	const fetchPieChartData = async (currentValue: string) => {
		try {
			setPieChartDataLoading(true)

			if (currentValue === '1') {
				const payload = {
					rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
					clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode ?? "" : investorClientId,
					portfolioId: !initialRouter.includes('product') ? clientInterfaceCode ?? selectedPortfolioValue : "",
					assetGroup: "EQ"
				}

				const { result, message = "", isSuccess } = await (dispatch(getMarketCapAllocation(payload)) as unknown as Promise<MarketCapAllocationRes>);

				if (isSuccess) {
					setPieChartData({ isSuccess: true, ...result })
				} else {
					setPieChartData({
						isSuccess: false,
						data: [],
						labelData: [],
						totalValue: { label: '', value: 0 }
					})
				}

			} else if (currentValue === '2') {
				const payload = {
					rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
					clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode ?? "" : investorClientId,
					portfolioId: !initialRouter.includes('product') ? clientInterfaceCode ?? selectedPortfolioValue : "",
					assetGroup: "EQ"
				}

				const { result, message = "", isSuccess } = await (dispatch(getSectorAllocation(payload)) as unknown as Promise<SectorAllocationResponseData>);

				if (isSuccess) {
					setPieChartData({ isSuccess: true, ...result })
				} else {
					setPieChartData({
						isSuccess: false,
						data: [],
						labelData: [],
						totalValue: { label: '', value: 0 }
					})
				}
			}

		} catch (error) {
			console.log("Error: ", error)
		} finally {
			setPieChartDataLoading(false)
		}

	}

	const fetchTopFivePerformersData = async () => {
		try {
			setTopFivePerformersDataLoading(true)

			const payload = {
				rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
				category: "",
				clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode ?? "" : investorClientId,
				portfolioId: !initialRouter.includes('product') ? clientInterfaceCode ?? selectedPortfolioValue : "",
				assetGroup: "EQ",
				isTop: true
			}

			const { result, message = "", isSuccess } = await (dispatch(top10Schemes(payload)) as unknown as Promise<GetTop10SchemesRes>)

			if (isSuccess) {
				const { topSchemes = { data: [] } } = result
				const { data = [] } = topSchemes

				setTopFivePerformersData({
					isSuccess: true,
					data
				})
			} else {
				setTopFivePerformersData({
					isSuccess: false,
					data: []
				})
			}

		} catch (error) {
			console.log("Error: ", error)
		} finally {
			setTopFivePerformersDataLoading(false)
		}
	}

	const fetchBottomFivePerformersData = async () => {
		try {
			setBottomFivePerformersDataLoading(true)

			const payload = {
				rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
				category: "",
				clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode ?? "" : investorClientId,
				portfolioId: !initialRouter.includes('product') ? clientInterfaceCode ?? selectedPortfolioValue : "",
				assetGroup: "EQ",
				isTop: false
			}

			const { result, message = "", isSuccess } = await (dispatch(top10Schemes(payload)) as unknown as Promise<GetTop10SchemesRes>)

			if (isSuccess) {
				const { topSchemes = { data: [] } } = result
				const { data = [] } = topSchemes

				setBottomFivePerformersData({
					isSuccess: true,
					data
				})

			} else {
				setBottomFivePerformersData({
					isSuccess: false,
					data: []
				})
			}

		} catch (error) {
			console.log("Error: ", error)
		} finally {
			setBottomFivePerformersDataLoading(false)
		}
	}

	const fetchDirectEquityPerformance = async () => {
		try {
			setPerformanceLoading(true);
			const payload = {
				rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
				clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode ?? "" : investorClientId,
				portfolioId: !initialRouter.includes('product') ? clientInterfaceCode ?? selectedPortfolio?.value : "",
				assetGroup: 'EQ',
			}
			const { isSuccess, message = "", result = {
				data1: [],
				data2: [],
				data3: [],
				data4: [],
				data5: [],
				label1: "",
				label2: "",
				label3: "",
				label4: "",
				label5: "",
			} } = await (dispatch(getBenchmarkPerformance(payload)) as unknown as Promise<BenchmarkPerformanceRes>)

			if (isSuccess) {
				setPerformanceData(({
					isSuccess: true,
					data1: result?.data1 ?? [],
					data2: result?.data2 ?? [],
					data3: result?.data3 ?? [],
					data4: result?.data4 ?? [],
					data5: result?.data5 ?? [],
					label1: result?.label1 ?? "",
					label2: result?.label2 ?? "",
					label3: result?.label3 ?? "",
					label4: result?.label4 ?? "",
					label5: result?.label5 ?? "",
				}))

				setFilteredPerformanceData(({
					isSuccess: true,
					data1: result?.data1 ?? [],
					data2: result?.data2 ?? [],
					data3: result?.data3 ?? [],
					data4: result?.data4 ?? [],
					data5: result?.data5 ?? [],
					label1: result?.label1 ?? "",
					label2: result?.label2 ?? "",
					label3: result?.label3 ?? "",
					label4: result?.label4 ?? "",
					label5: result?.label5 ?? "",
				}))

			} else {
				setPerformanceData(({
					isSuccess: false,
					data1: [],
					data2: [],
					data3: [],
					data4: [],
					data5: [],
					label1: "",
					label2: "",
					label3: "",
					label4: "",
					label5: "",
				}))

				setFilteredPerformanceData(({
					isSuccess: false,
					data1: [],
					data2: [],
					data3: [],
					data4: [],
					data5: [],
					label1: "",
					label2: "",
					label3: "",
					label4: "",
					label5: "",
				}))
			}
		}
		catch (e) {
			console.log('Error', e);
		} finally {
			setPerformanceLoading(false)
		}
	}

	const fetchHoldingDetailsFilter = async () => {
		try {
			setHoldingTableHeadersLoading(true)

			const response = await (dispatch(getHoldingDetailsFilter()) as unknown as Promise<GetHoldingDetailsFilterResponseBody>) || []

			const modifiedData = response.flatMap(({ category, filters }) =>
				filters.map(({ id, value }) => ({
					id,
					label: value,
					align: 'center',
					category,
					isSortable: true,
					checked: initialSelectedHoldingTableHeadings.includes(id),
					headerTableCellStyle: { minWidth: "200px" }
				} as TableHeaderData))
			);

			setHoldingTableHeaders(modifiedData)
			setAppliedHoldingTableHeaders(modifiedData.filter((headerObj: TableHeaderData) => headerObj.checked))
		} catch (error) {
			console.log("Error: ", error)
		} finally {
			setHoldingTableHeadersLoading(false)
		}
	}

	const fetchHoldingDetailsData = async ({
		sortItem = "marketValue",
		order = "desc",
		currentPage = 1,
		rowsPerPage = 5
	}: FetchHoldingDetailsDataArgs = {}) => {
		try {
			setHoldingTableDataLoading(true)

			const modifiedSortItem = sortItem === "percentage" ? "unrealisedAmount" : sortItem

			const payload = {
				rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
				sortItem: modifiedSortItem,
				order,
				assetGroup: "EQ",
				currentPage,
				rowsPerPage,
				clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode ?? "" : investorClientId,
				portfolioId: !initialRouter.includes('product') ? clientInterfaceCode ?? selectedPortfolioValue : "",
			}

			const { result = [], message = "", isSuccess, totalLength = 0 } = await (dispatch(getSchemeDetails(payload)) as unknown as Promise<GetSchemeDetailsResponseBody>)


			if (isSuccess) {
				const modifiedResult = result.map((obj) => {
					const displayFolioNum = ((`${obj?.folioNo ?? ""}`?.length) && (`${obj?.folioNo ?? ""}` !== '0')) ? `(${obj?.folioNo ?? ""})` : ""

					return ({
						...obj,
						schemeName: obj?.schemeName ? `${obj?.schemeName} ${displayFolioNum}` : "-"
					})
				})
				setHoldingTableData({ isSuccess: true, data: modifiedResult })
				setHoldingPaginationObj(prevState => ({
					...prevState,
					totalRows: totalLength
				}))
			} else {
				setHoldingTableData({ isSuccess: false, data: [] })
				setHoldingPaginationObj({
					currentPage: 0,
					rowsPerPage: 5,
					totalRows: 0
				})
			}

		} catch (error) {
			console.log("Error: ", error)
		} finally {
			setHoldingTableDataLoading(false)
		}

	}

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true)
			const isSuccess = await fetchDirectEquityData()

			if (isSuccess) {
				if (selectedTab === "Overview") {
					await Promise.allSettled([
						fetchPieChartData('1'),
						fetchTopFivePerformersData(),
						fetchBottomFivePerformersData(),
						fetchDirectEquityPerformance()
					])
				}
				if (selectedTab === "Holding Details") {
					await Promise.allSettled([
						fetchHoldingDetailsFilter(),
						fetchHoldingDetailsData()
					])
				}
				if (selectedTab === "Transaction Details") {
					await fetchTransactionDetailsData()
				}
			}

			setLoading(false)
		}

		fetchData()
	}, [])

	const handleTabChange = async (currentTab: string) => {
		setSelectedTab(currentTab)

		if (currentTab === "Overview") {
			setActivePerformanceChartButton("Inception")
			setSelectedDropdownOption("1")

			await Promise.allSettled([
				fetchPieChartData('1'),
				fetchTopFivePerformersData(),
				fetchBottomFivePerformersData(),
				fetchDirectEquityPerformance()
			])
		}

		if (currentTab === "Holding Details") {
			setHoldingSortingObj({
				order: "desc",
				orderBy: "marketValue"
			})
			setHoldingPaginationObj({
				currentPage: 0,
				rowsPerPage: 5,
				totalRows: 0
			})

			await Promise.allSettled([
				fetchHoldingDetailsFilter(),
				fetchHoldingDetailsData()
			])
		}

		if (currentTab === "Transaction Details") {
			await Promise.allSettled([
				fetchTransactionDetailsData()
			])
		}

		history.replace({
			pathname: location.pathname, search: `?tab=${currentTab.replace("&", "%26")}`, state: { clientCode }
		})
	}

	const currentInvestedValueObj = directEquityDataObj
		.data
		.find(obj => obj.title === "Invested Value")
	const portfolioNotExistStatus = Number(currentInvestedValueObj?.value ?? 0) === 0

	return (
		<Box sx={{ marginTop: "15px" }}>
			<BreadCrum array={![USER_ROLES.INVESTOR].includes(role) ? rmRouterPath : investorRouterPath} />
			<Grid
				container
				sx={{
					marginTop: "25px",
					display: "flex",
				}}
				spacing={2}
			>
				{directEquityDataObj.data.map((obj: DirectEquityData) => (
					<Grid
						item
						key={obj.title}
						xs={12}
						md={6}
						lg={3}
					>
						<InfoCard
							Icon={obj.Icon}
							title={obj.title}
							value={obj.value}
							percentage={obj?.percentage}
							valueTextColor={obj?.valueTextColor}
							isLoading={isDirectEquityDataArrayLoading}
						/>
					</Grid>
				))}
			</Grid>

			<Box
				sx={(theme: Theme) => ({
					display: 'flex',
					gap: '10px',
					paddingBottom: '15px',
					borderBottom: `1px solid ${theme.palette.customColors.pastelGrey}`,
					overflowX: 'auto',
					marginTop: "50px"
				})}
			>
				{tabsList.map((item) => {
					return (
						<Button
							variant={selectedTab === item ? "contained" : "text"}
							key={item}
							onClick={() => handleTabChange(item)}
							sx={(theme: Theme) => ({
								padding: '5px 15px',
								background: selectedTab === item ? theme.palette.primary[500] : 'transparent',
								borderRadius: '7px',
								outline: 'none',
								border: 'none',
								cursor: 'pointer'
							})}
							disabled={item === "F&ODetails" || portfolioNotExistStatus}
						>
							<Typography
								variant="body_500"
								sx={(theme: Theme) => ({
									color: selectedTab === item ? theme.palette.textDark.primary : theme.palette.text.secondary
								})}
							>
								{item}
							</Typography>
						</Button>
					)
				})}
			</Box>
			<Box sx={{ marginTop: "25px" }}>
				{(!isLoading && portfolioNotExistStatus) ?
					<Box
						sx={(theme: Theme) => ({
							backgroundColor: theme.palette.common.white,
							borderRadius: "25px",
							padding: "25px 30px",
							height: "50vh"
						})}
					>
						<ZeroStateInvestment isSuccess={directEquityDataObj.isSuccess} />
					</Box>
					:
					(
						<>
							{selectedTab === 'Overview' && (
								<DirectEquityOverview
									isLoading={isLoading}
									selectedDropdownOption={selectedDropdownOption}
									setSelectedDropdownOption={setSelectedDropdownOption}
									fetchPieChartData={fetchPieChartData}
									setActivePerformanceChartButton={setActivePerformanceChartButton}
									setFilteredPerformanceData={setFilteredPerformanceData}
									performanceData={performanceData}
									isPieChartDataLoading={isPieChartDataLoading}
									pieChartData={pieChartData}
									isPerformanceLoading={isPerformanceLoading}
									filteredPerformanceData={cloneDeep(filteredPerformanceData)}
									activePerformanceChartButton={activePerformanceChartButton}
									isTopFivePerformersDataLoading={isTopFivePerformersDataLoading}
									topFivePerformersData={topFivePerformersData}
									isBottomFivePerformersDataLoading={isBottomFivePerformersDataLoading}
									bottomFivePerformersData={bottomFivePerformersData}
									open={open}
									setOpen={setOpen}
								/>
							)}
							{selectedTab === 'Holding Details' && (
								<DirectEquityHoldingDetails
									isLoading={isLoading}
									sortingObj={holdingSortingObj}
									setSortingObj={setHoldingSortingObj}
									fetchHoldingDetailsData={fetchHoldingDetailsData}
									paginationObj={holdingPaginationObj}
									setPaginationObj={setHoldingPaginationObj}
									tableHeaders={holdingTableHeaders}
									setTableHeaders={setHoldingTableHeaders}
									appliedTableHeaders={appliedHoldingTableHeaders}
									setAppliedTableHeaders={setAppliedHoldingTableHeaders}
									isFilterDrawerOpen={isHoldingFilterDrawerOpen}
									setFilterDrawerOpen={setHoldingFilterDrawerOpen}
									isTableHeadersLoading={isHoldingTableHeadersLoading}
									isTableDataLoading={isHoldingTableDataLoading}
									tableData={holdingTableData}
								/>
							)}
							{selectedTab === 'Transaction Details' && (
								<NonMFTransactionDetails
									transactionDetailsLoading={transactionDetailsLoading}
									setTransactionDetailsLoading={setTransactionDetailsLoading}
									transactionTableData={transactionTableData}
									setTransactionTableData={setTransactionTableData}
									transactionDetailPaginationObj={transactionDetailPaginationObj}
									setTransactionDetailPaginationObj={setTransactionDetailPaginationObj}
									fetchTransactionDetailsData={fetchTransactionDetailsData}
									transactionDetailsData={transactionDetailsData}
									allTransactionsData={allTransactionsData}
								/>
							)}
						</>
					)
				}
			</Box>
		</Box >
	)
}